import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Oct 2026 — 12-month 7-day net yield sparkline for a money-market fund,
 * built from the daily readings inside each monthly SEC N-MFP filing
 * (`/api/stocks/:t/mmf-yield-history`). Pure SVG, hover shows the day.
 */
export default function MmfYieldSparkline({ ticker, months = 12, className = '', testId = 'mmf-yield-sparkline' }) {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);
  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setData(null);
    axios.get(`${API}/stocks/${encodeURIComponent(ticker)}/mmf-yield-history`, { params: { months } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ points: [] }); });
    return () => { cancelled = true; };
  }, [ticker, months]);

  const pts = data?.points || [];
  if (data == null) return <div className={`h-[72px] rounded-md bg-cyan-100/40 dark:bg-cyan-900/20 animate-pulse ${className}`} data-testid={`${testId}-loading`} />;
  if (pts.length < 2) return null;

  const W = 320; const H = 64; const PAD = 4;
  const ys = pts.map((p) => p.yield_pct);
  const minY = Math.min(...ys); const maxY = Math.max(...ys);
  const span = Math.max(maxY - minY, 0.05);
  const x = (i) => PAD + (i / (pts.length - 1)) * (W - PAD * 2);
  const y = (v) => H - PAD - ((v - minY) / span) * (H - PAD * 2);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.yield_pct).toFixed(1)}`).join(' ');
  const area = `${path} L${x(pts.length - 1).toFixed(1)},${H - PAD} L${x(0).toFixed(1)},${H - PAD} Z`;
  const first = pts[0]; const last = pts[pts.length - 1];
  const chg = last.yield_pct - first.yield_pct;
  const fmtD = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  const hv = hover != null ? pts[hover] : null;
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rel = (e.clientX - rect.left) / rect.width;
    setHover(Math.max(0, Math.min(pts.length - 1, Math.round(rel * (pts.length - 1)))));
  };

  return (
    <div className={`rounded-md border border-cyan-300/60 dark:border-cyan-800/60 bg-white/60 dark:bg-cyan-950/30 px-3 py-2 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 text-[10px] font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">
        <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> 7-Day Yield · last {months} months</span>
        <span className="font-mono normal-case tracking-normal" data-testid={`${testId}-summary`}>
          {hv ? `${fmtD(hv.date)} · ${hv.yield_pct.toFixed(2)}%` : `${first.yield_pct.toFixed(2)}% → ${last.yield_pct.toFixed(2)}% (${chg >= 0 ? '+' : ''}${chg.toFixed(2)} pt)`}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16 mt-1 cursor-crosshair" preserveAspectRatio="none"
        onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img" aria-label={`${ticker} 7-day yield history`}>
        <path d={area} fill="currentColor" className="text-cyan-500/15" />
        <path d={path} fill="none" stroke="currentColor" strokeWidth="1.6" className="text-cyan-600 dark:text-cyan-300" vectorEffect="non-scaling-stroke" />
        {hv && (
          <>
            <line x1={x(hover)} x2={x(hover)} y1={PAD} y2={H - PAD} stroke="currentColor" strokeWidth="1" className="text-cyan-700/60" vectorEffect="non-scaling-stroke" />
            <circle cx={x(hover)} cy={y(hv.yield_pct)} r="3" fill="currentColor" className="text-cyan-700 dark:text-cyan-200" />
          </>
        )}
      </svg>
      <div className="flex justify-between text-[10px] text-cyan-700/80 dark:text-cyan-400/80 font-mono">
        <span>{fmtD(first.date)}</span>
        <span>low {minY.toFixed(2)}% · high {maxY.toFixed(2)}%</span>
        <span>{fmtD(last.date)}</span>
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5">Source: SEC Form N-MFP, daily 7-day net yield readings ({pts.length} points)</div>
    </div>
  );
}
